import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { Button, Autocomplete, TextField, Dialog } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { useTranslation } from 'react-i18next';
import SearchPartnerItemComponent from './SearchPartnerItemComponent';
import { emptyArray } from 'constants/defaultValues';
import { get, set } from '@totalsoft/change-tracking-react';
import { CardActionsContainer } from 'components/common/CardStyles';

const SearchPartnerComponent = ({
  partners,
  filterLens,
  isPF,
  citizenships,
  citizenshipsLoading,
  cities,
  citiesLoading,
  counties,
  countiesLoading,
  onCountyChange,
  onToggleSelectItem,
  onCloseModal,
  onCopyPartner,
  pager,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
  Pagination
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      id='search-partner-dialog'
      open
      fullWidth
      maxWidth={'xl'}
      onClose={onCloseModal}
      content={
        <>
          <Grid container justifyContent={'center'}>
            {!isPF ? (
              <Grid container justifyContent={'center'} spacing={3}>
                <Table id={'legalPartners_list'}>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>
                        <strong>{t('Partner.UniqueID.CUI')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('Partner.Name')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('Partner.Search.LegalForm')}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>
                        <TextField
                          placeholder={t('Partner.Search.Filter')}
                          fullWidth={true}
                          value={filterLens.PJ.uniqueId |> get}
                          onChange={filterLens.PJ.uniqueId |> set}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          placeholder={t('Partner.Search.Filter')}
                          fullWidth={true}
                          value={filterLens.PJ.name |> get}
                          onChange={filterLens.PJ.name |> set}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partners &&
                      partners.map((partner, index) => (
                        <SearchPartnerItemComponent key={index} item={partner} isPF={isPF} onToggleSelectItem={onToggleSelectItem} />
                      ))}
                  </TableBody>
                </Table>
              </Grid>
            ) : (
              <Grid container justifyContent={'center'} spacing={3}>
                <Table id={'individualPartners_list'}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '15px' }} />
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.Name')}</strong>
                      </TableCell>
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.Search.Citizenship')}</strong>
                      </TableCell>
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.Search.IdSeriesAndNumber')}</strong>
                      </TableCell>
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.Search.BirthDate')}</strong>
                      </TableCell>
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.Search.Country')}</strong>
                      </TableCell>
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.District')}</strong>
                      </TableCell>
                      <TableCell sx={{ width: '150px' }}>
                        <strong>{t('Partner.City')}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell />
                      <TableCell>
                        <TextField
                          id='partnerModal_nameFilter'
                          placeholder={t('Partner.Search.Filter')}
                          fullWidth={false}
                          value={filterLens.PF.fullName |> get}
                          onChange={filterLens.PF.fullName |> set}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <Autocomplete
                            id='partnerModal_citizenshipFilter'
                            label={t('Partner.Search.Filter')}
                            isSearchable={true}
                            isClearable={true}
                            fullWidth
                            valueKey='id'
                            labelKey='name'
                            value={filterLens.PF.citizenshipId |> get}
                            onChange={filterLens.PF.citizenshipId |> set}
                            options={citizenships || emptyArray}
                            disabled={citizenshipsLoading}
                            simpleValue={true}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <TextField
                          id='partnerModal_idSeriesAndNumberFilter'
                          placeholder={t('Partner.Search.Filter')}
                          fullWidth={false}
                          value={filterLens.PF.idSeriesAndNumber |> get}
                          onChange={filterLens.PF.idSeriesAndNumber |> set}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <DateTime
                            id='partnerModal_birthDateFilter'
                            isClearable={true}
                            required={false}
                            changeOnInvalid={false}
                            label={t('Partner.Search.Filter')}
                            value={filterLens.PF.birthDate |> get}
                            onChange={filterLens.PF.birthDate |> set}
                            timeFormat={false}
                          />
                        </div>
                      </TableCell>
                      <TableCell />
                      <TableCell>
                        <div>
                          <Autocomplete
                            id='partnerModal_countyFilter'
                            label={t('Partner.Search.Filter')}
                            isSearchable={true}
                            isClearable={true}
                            fullWidth
                            valueKey='id'
                            labelKey='name'
                            value={filterLens.PF.countyId |> get}
                            onChange={onCountyChange}
                            options={counties || emptyArray}
                            disabled={countiesLoading}
                            simpleValue={true}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <Autocomplete
                            id='partnerModal_cityFilter'
                            label={t('Partner.Search.Filter')}
                            isSearchable={true}
                            isClearable={true}
                            fullWidth
                            valueKey='id'
                            labelKey='name'
                            value={filterLens.PF.cityId |> get}
                            onChange={filterLens.PF.cityId |> set}
                            options={cities || emptyArray}
                            disabled={citiesLoading}
                            simpleValue={true}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partners &&
                      partners.map((partner, index) => (
                        <SearchPartnerItemComponent key={index} item={partner} isPF={isPF} onToggleSelectItem={onToggleSelectItem} />
                      ))}
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>
          <Grid container justifyContent={'center'}>
            <Pagination
              totalCount={pager.totalCount}
              pageSize={pager.pageSize}
              page={pager.page}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              onRefresh={onRefresh}
            />
          </Grid>
        </>
      }
      actions={
        <CardActionsContainer mb={1} mr={1}>
          <Button id='partnerModal_selectButton' color={'primary'} disabled={false} onClick={onCopyPartner}>
            {t('General.Select')}
          </Button>
          <Button color={'primary'} onClick={onCloseModal}>
            {t('General.Close')}
          </Button>
        </CardActionsContainer>
      }
    />
  );
};

SearchPartnerComponent.propTypes = {
  partners: PropTypes.array,
  filterLens: PropTypes.object,
  isPF: PropTypes.bool.isRequired,
  citizenships: PropTypes.array,
  citizenshipsLoading: PropTypes.bool,
  cities: PropTypes.array,
  citiesLoading: PropTypes.bool.isRequired,
  counties: PropTypes.array,
  countiesLoading: PropTypes.bool.isRequired,
  loadingIndividualPartners: PropTypes.bool.isRequired,
  loadingLegalPartners: PropTypes.bool.isRequired,
  onCountyChange: PropTypes.func.isRequired,
  onToggleSelectItem: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onCopyPartner: PropTypes.func.isRequired,
  pager: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  Pagination: PropTypes.elementType.isRequired
};

export default SearchPartnerComponent;
